import {FactoryRegistry, House, Msg, State} from "lib/edit/ot/urban";
import {MsgOt} from "lib/edit/ot/houseOt";
import {EAnnotLevel, ISkAnnot} from "lib/edit/schema/schemaAnnots";
import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {IXmlPrgSensitiveMsg, XmlAddrMsg, XmlBodyState, XmlListMsgOt} from "lib/edit/ot/xmlHouse";
import {XmlTypedHouse} from "lib/edit/ot/xmlTypedHouse";

export interface IMoanState extends State {

	fillAllAnnots(an: ISkAnnot[]): void

	fillAnnots(an: ISkAnnot[], xa: IXAddr, length?: number, deep?: boolean): void
}

/**
 * Annotations Moan rassemblés dans un State de la house.
 */
export class MoanState extends State implements IMoanState {
	static readonly type = "moan";

	annots: Map<number, MoanAnnot> = new Map();

	fillAllAnnots(an: ISkAnnot[]) {
		for (const m of this.annots.values()) {
			if (!m.killed) an.push(m);
		}
	}

	fillAnnots(an: ISkAnnot[], xa: IXAddr, length: number = 1, deep: boolean = true) {
		for (const m of this.annots.values()) {
			if (!m.killed && XA.isInSeq(xa, length, m.addr)) {
				if (deep || m.addr.length === xa.length - 1) an.push(m);
			}
		}
	}

	update(msg: Msg): void {
		if (msg instanceof MoanListAnnotMsg) {
			//Mise à jour de la liste des annots.
			const del: ISkAnnot[] = [];
			if (msg.remAnnots) for (const id of msg.remAnnots) {
				const m = this.annots.get(id);
				if (m) {
					this.annots.delete(id);
					del.push(m);
				}
			}
			const domRoot = (this.house.getStateById("body") as XmlBodyState).root;
			for (const m of (msg.msgs as MoanAnnot[])) {
				m.bindToDom(domRoot);
				this.annots.set(m.moanId, m);
			}
			(this.house as XmlTypedHouse).listeners.emit("onSkAnnotsChange", del, msg.msgs as MoanAnnot[]);
		} else if (msg instanceof MsgOt && msg.isBodyMutator) {
			//Ajustement des xa essentiellement pour kill les annots des noeuds supprimés.
			let del: ISkAnnot[];
			for (const m of this.annots.values()) {
				if (!m.killed) {
					msg.adjust(m);
					if (m.killed) {
						if (!del) del = [m];
						else del.push(m);
					}
				}
			}
			if (del) (this.house as XmlTypedHouse).listeners.emit("onSkAnnotsChange", del, null);
		}
	}

	fromJson(json: any, factoryReg: FactoryRegistry<State>) {
		super.fromJson(json, factoryReg)
		for (const m of json.annots) {
			const msg = House.MSG_FACTORYREG.newFromJson(m) as MoanAnnot;
			this.annots.set(msg.moanId, msg);
		}
	}

	attachHouse(house: House | null): this {
		super.attachHouse(house);
		if (house) {
			const domRoot = (house.getStateById("body") as XmlBodyState).root;
			for (const m of this.annots.values()) m.bindToDom(domRoot);
		} else {
			this.annots.clear();
		}
		return this;
	}

}

House.STATE_FACTORYREG.register(MoanState);

/**
 * Msg de la house, qui une fois inséré dans le MoanState est vu comme une ISkAnnot immutable.
 */
export class MoanAnnot extends XmlAddrMsg implements ISkAnnot, IXmlPrgSensitiveMsg {
	static readonly type: string = "moanAnnot";

	/** Id stable de l'annot par rapport à l'ancrage à anchorNode (xa peut évoluer). */
	moanId: number

	anchorNode: Node;

	label: string
	level: EAnnotLevel;

	init(addr: IXAddr): this {throw "Don't use!"}

	initMoan(addr: IXAddr, moanId: number, label: string, level: EAnnotLevel): this {
		super.init(addr);
		this.moanId = moanId;
		this.label = label;
		this.level = level;
		return this;
	}

	bindToDom(root: Node) {
		this.anchorNode = XA.findDomLast(this.addr, root);
	}

	get start(): IXAddr {return this.anchorNode ? XA.from(this.anchorNode) : this.addr}

	invert(): MoanAnnot {return new MoanAnnot().initMoan(this.addr, this.moanId, this.label, this.level).copyStableMetasFrom(this)}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.moanId = json.moanId;
		switch (json.level) {
		case "error":
			this.level = EAnnotLevel.error;
			break;
		case "warning":
			this.level = EAnnotLevel.warning;
			break;
		case "info":
			this.level = EAnnotLevel.info;
			break;
		default:
			this.level = EAnnotLevel.error;
		}
		this.label = json.label;
	}

	//------- Annot Api

	get len(): number {return 1}

	getLabel(): string {return this.label}

	equals(other: ISkAnnot): boolean {
		return other instanceof MoanAnnot && this.moanId === other.moanId;
	}

	toJSON(): any {throw "TODO"}
}

House.MSG_FACTORYREG.register(MoanAnnot);


class MoanListAnnotMsg extends XmlListMsgOt implements IXmlPrgSensitiveMsg {
	static readonly type = "moanListAnnot";

	/** Liste de moanId à supprimer. */
	remAnnots?: number[]

	get isBodyMutator(): boolean {return false}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.remAnnots = json["remAnnots"];
	}

	toJson(json: any) {
		super.toJson(json);
		json["remAnnots"] = this.remAnnots;
	}
}

House.MSG_FACTORYREG.register(MoanListAnnotMsg);

